// Map editor top-level screen. Three-pane layout:
//
//   ┌──────────────────────────────────────────────────────────────────────┐
//   │  TOOLBAR (toolbar buttons + map dropdown + actions)                  │
//   ├──────────┬───────────────────────────────────┬───────────────────────┤
//   │  TREE    │  VIEWPORT (free-fly cam + gizmo)  │  INSPECTOR            │
//   │  (260px) │  (flex)                           │  (340px)              │
//   ├──────────┴───────────────────────────────────┴───────────────────────┤
//   │  STATUS                                                              │
//   └──────────────────────────────────────────────────────────────────────┘

import { listBundles, getBundle } from '../maps/registry';
import type { MapJSON, ObjectNode, BoxObject, SphereObject, CylinderObject, CapsuleObject, PlaneObject, CircleObject, GroupObject, ModelRefObject, LightDef } from '../maps/MapJSON';
import { EditorState } from './editor/EditorState';
import { EditorViewport } from './editor/EditorViewport';
import { ObjectTreePane } from './editor/ObjectTreePane';
import { InspectorPane } from './editor/InspectorPane';
import { Toolbar } from './editor/Toolbar';
import { ViewportOverlay } from './editor/ViewportOverlay';
import { EditorTool } from './editor/EditorTool';
import { exportBundleToBlob, triggerDownload } from './editor/exportZip';
import { importBundleFromFile } from './editor/importZip';
import { registerAssetFromFile, makeAssetUrl, resolveUrl as resolveAssetUrl, isAssetUrl, disposeAll as disposeAllAssets } from './editor/EditorAssets';
import { menuAudio } from './MenuAudio';
import editorThemeCss from './editor/theme.css?inline';

export interface MapEditorCallbacks {
  /** Called when the user hits Playtest. Receives the current in-memory
   *  json (a deep clone with `asset://` URLs already resolved to blob URLs
   *  for the production Renderer's consumers). The caller launches a match,
   *  awaits its end, and resolves. */
  onPlaytest(json: MapJSON): Promise<void>;
}

const STYLES = `
${editorThemeCss}

#map-editor {
  position: fixed; inset: 0; z-index: 100;
  display: grid;
  grid-template-columns: 260px 1fr 340px;
  grid-template-rows: 44px 1fr 28px;
  grid-template-areas:
    "top top top"
    "tree viewport inspector"
    "status status status";
}
`;

export class MapEditorScreen {
  private root: HTMLElement;
  private styleEl: HTMLStyleElement;
  private state: EditorState;
  private tool: EditorTool;
  private viewport!: EditorViewport;
  private tree!: ObjectTreePane;
  private inspector!: InspectorPane;
  private toolbar!: Toolbar;
  private overlay!: ViewportOverlay;
  private statusEl!: HTMLElement;
  private statusText!: HTMLElement;
  private statusDot!: HTMLElement;
  private clipboard: ObjectNode[] = [];
  private disposed = false;
  private cbs: MapEditorCallbacks;
  private resolveExit: (() => void) | null = null;
  private keyHandler = this.onKey.bind(this);

  constructor(cbs: MapEditorCallbacks) {
    this.cbs = cbs;

    const allBundles = listBundles();
    if (allBundles.length === 0) {
      throw new Error('[MapEditor] no map bundles available');
    }
    const initialBundle = allBundles.find((b) => !b.json.hiddenFromPicker) ?? allBundles[0]!;

    this.state = new EditorState({
      bundleId: initialBundle.id,
      json: structuredClone(initialBundle.json),
      selectionId: 'map',
      dirty: false
    });
    this.tool = new EditorTool();

    this.styleEl = document.createElement('style');
    this.styleEl.textContent = STYLES;
    document.head.appendChild(this.styleEl);

    this.root = document.createElement('div');
    this.root.id = 'map-editor';
    document.body.appendChild(this.root);

    // Toolbar
    this.toolbar = new Toolbar(this.root, this.state, this.tool, {
      onMapChange: (id) => {
        const b = getBundle(id);
        if (b) this.state.loadBundle(id, structuredClone(b.json));
      },
      onAddPrimitive: (kind) => this.addPrimitive(kind),
      onAddLight: (kind) => this.addLight(kind),
      onImportMesh: () => this.pickAndImportMesh(),
      onImportHdr: () => this.pickAndImportHdr(),
      onImportZip: () => this.onImportZip(),
      onExport: () => this.onExport(),
      onPlaytest: () => this.onPlaytest(),
      onExit: () => this.exit()
    });

    // Panes
    const left = document.createElement('div');
    left.className = 'ed-pane-tree';
    this.root.appendChild(left);
    const center = document.createElement('div');
    center.className = 'ed-pane-viewport';
    this.root.appendChild(center);
    const right = document.createElement('div');
    right.className = 'ed-pane-inspector';
    this.root.appendChild(right);

    // Status bar
    this.statusEl = document.createElement('div');
    this.statusEl.className = 'ed-status';
    this.root.appendChild(this.statusEl);
    this.statusDot = document.createElement('span');
    this.statusDot.className = 'dirty-dot';
    this.statusDot.style.display = 'none';
    this.statusEl.appendChild(this.statusDot);
    this.statusText = document.createElement('span');
    this.statusText.textContent = 'clean';
    this.statusEl.appendChild(this.statusText);

    this.tree = new ObjectTreePane(left, this.state);
    this.inspector = new InspectorPane(right, this.state);
    this.viewport = new EditorViewport(center, {
      onPickObject: (id, additive) => {
        if (!id) {
          if (!additive) this.state.clearSelection();
          return;
        }
        const sid = `obj:${id}`;
        if (additive) this.state.toggleSelection(sid);
        else this.state.setSelection([sid]);
      },
      onFileDrop: (files) => this.handleDroppedFiles(files)
    });

    this.state.subscribeDoc(() => this.applyDocStatus());
    this.applyDocStatus();
    addEventListener('keydown', this.keyHandler);
  }

  async show(): Promise<void> {
    await this.viewport.init();
    this.viewport.bind(this.state, this.tool);
    this.overlay = new ViewportOverlay(this.root.querySelector('.ed-pane-viewport')!, this.tool, this.viewport.fly, {
      onFrameSelection: () => { if (!this.viewport.frameSelection()) this.flash('Nothing selected to frame', true); }
    });
    // Start the render loop and trigger the initial bundle load via a
    // structural commit so all listeners see the apply path.
    this.viewport.start();
    await this.viewport.loadBundle(this.state.get().bundleId, this.state.get().json);
    // Per-frame overlay sync
    const tickOverlay = () => {
      if (this.disposed) return;
      this.overlay.step();
      requestAnimationFrame(tickOverlay);
    };
    requestAnimationFrame(tickOverlay);

    menuAudio.stopMusic();
    menuAudio.resetFilter();
    await new Promise<void>((resolve) => { this.resolveExit = resolve; });
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    removeEventListener('keydown', this.keyHandler);
    this.viewport.dispose();
    this.overlay.dispose();
    this.tree.dispose();
    this.inspector.dispose();
    this.toolbar.dispose();
    this.root.remove();
    this.styleEl.remove();
    disposeAllAssets();
  }

  // ---------- status bar ----------

  private applyDocStatus(): void {
    const s = this.state.get();
    this.statusDot.style.display = s.dirty ? 'inline-block' : 'none';
    this.statusText.textContent = s.dirty ? 'unsaved edits' : 'clean';
  }

  private flash(message: string, isError = false): void {
    this.overlay?.flash(message, isError);
  }

  // ---------- file IO ----------

  private async handleDroppedFiles(files: FileList): Promise<void> {
    for (let i = 0; i < files.length; i++) {
      const f = files[i]!;
      const lower = f.name.toLowerCase();
      try {
        if (lower.endsWith('.glb') || lower.endsWith('.gltf')) {
          const asset = await registerAssetFromFile(f);
          this.spawnModelRef(makeAssetUrl(asset.id), f.name);
          this.flash(`Imported mesh ${f.name}`);
        } else if (lower.endsWith('.hdr')) {
          const asset = await registerAssetFromFile(f);
          const json = this.state.get().json;
          const ibl = { ...(json.ibl ?? {}), mode: 'hdr' as const, hdrPath: makeAssetUrl(asset.id) };
          this.state.commit({ ...json, ibl }, { kind: 'structural' });
          this.flash(`Imported HDR ${f.name}`);
        } else if (lower.endsWith('.zip')) {
          const result = await importBundleFromFile(f);
          this.state.loadBundle(result.bundleId, result.json);
          this.flash(`Imported bundle ${result.bundleId}`);
        } else {
          this.flash(`Unsupported file: ${f.name}`, true);
        }
      } catch (err) {
        console.error('[MapEditor] file drop failed:', err);
        this.flash(`Drop failed: ${(err as Error).message}`, true);
      }
    }
  }

  private async pickAndImportMesh(): Promise<void> {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.glb,.gltf,model/gltf-binary,model/gltf+json';
    input.onchange = async () => {
      const f = input.files?.[0];
      if (!f) return;
      const asset = await registerAssetFromFile(f);
      this.spawnModelRef(makeAssetUrl(asset.id), f.name);
      this.flash(`Imported mesh ${f.name}`);
    };
    input.click();
  }

  private async pickAndImportHdr(): Promise<void> {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.hdr';
    input.onchange = async () => {
      const f = input.files?.[0];
      if (!f) return;
      const asset = await registerAssetFromFile(f);
      const json = this.state.get().json;
      const ibl = { ...(json.ibl ?? {}), mode: 'hdr' as const, hdrPath: makeAssetUrl(asset.id) };
      this.state.commit({ ...json, ibl }, { kind: 'structural' });
      this.flash(`Imported HDR ${f.name}`);
    };
    input.click();
  }

  private onImportZip(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.zip,application/zip';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const result = await importBundleFromFile(file);
        if (!result.hasBaseBundle) {
          this.flash(`Imported "${result.bundleId}" — no base bundle on disk; behaviors may not resolve on Playtest.`, true);
        } else {
          this.flash(`Imported "${result.bundleId}"`);
        }
        this.state.loadBundle(result.bundleId, result.json);
      } catch (err) {
        console.error('[MapEditor] import failed:', err);
        this.flash('Import failed: ' + (err as Error).message, true);
      }
    };
    input.click();
  }

  private async onExport(): Promise<void> {
    const { bundleId, json } = this.state.get();
    try {
      const blob = await exportBundleToBlob(bundleId, json);
      triggerDownload(blob, `${bundleId}.zip`);
      this.flash(`Exported ${bundleId}.zip`);
    } catch (err) {
      console.error('[MapEditor] export failed:', err);
      this.flash('Export failed — see console', true);
    }
  }

  // ---------- playtest ----------

  private async onPlaytest(): Promise<void> {
    this.toolbar.setPlaytestEnabled(false);
    this.viewport.pause();
    this.root.style.display = 'none';
    try {
      const snapshot = structuredClone(this.state.get().json);
      // Resolve any session asset:// URLs to blob URLs before handing to the
      // production Renderer + MapArenaView pipeline. The MapArenaView's
      // generic modelRef loader and Renderer.loadHdrEnv both also call
      // resolveUrl directly, so this is belt-and-braces.
      resolveAssetUrlsInPlace(snapshot);
      await this.cbs.onPlaytest(snapshot);
    } catch (err) {
      console.error('[MapEditor] playtest failed:', err);
    } finally {
      if (!this.disposed) {
        this.root.style.display = '';
        this.viewport.resume();
        this.toolbar.setPlaytestEnabled(true);
      }
    }
  }

  // ---------- authoring actions ----------

  private addPrimitive(kind: 'box' | 'sphere' | 'cylinder' | 'capsule' | 'plane' | 'circle' | 'group'): void {
    const json = this.state.get().json;
    const id = nextUniqueId(json, kind);
    const p = this.viewport.spawnTarget();
    const transform = { p } as const;
    let node: ObjectNode;
    switch (kind) {
      case 'box':      node = { type: 'box', id, size: [1, 1, 1], transform, material: { color: '#cccccc', roughness: 0.8 }, collider: { kind: 'auto', body: 'static' } } as BoxObject; break;
      case 'sphere':   node = { type: 'sphere', id, radius: 0.5, transform, material: { color: '#cccccc', roughness: 0.8 }, collider: { kind: 'auto', body: 'static' } } as SphereObject; break;
      case 'cylinder': node = { type: 'cylinder', id, radiusTop: 0.5, radiusBottom: 0.5, height: 1, transform, material: { color: '#cccccc', roughness: 0.8 }, collider: { kind: 'auto', body: 'static' } } as CylinderObject; break;
      case 'capsule':  node = { type: 'capsule', id, radius: 0.4, halfHeight: 0.5, transform, material: { color: '#cccccc', roughness: 0.8 }, collider: { kind: 'auto', body: 'static' } } as CapsuleObject; break;
      case 'plane':    node = { type: 'plane', id, size: [4, 4], transform, material: { color: '#cccccc', roughness: 0.95 }, collider: { kind: 'auto', body: 'static' } } as PlaneObject; break;
      case 'circle':   node = { type: 'circle', id, radius: 1, transform, material: { color: '#cccccc' } } as CircleObject; break;
      case 'group':    node = { type: 'group', id, transform, children: [] } as GroupObject; break;
    }
    this.state.commit({ ...json, objects: [...(json.objects ?? []), node] }, { kind: 'structural' });
    this.state.setSelection([`obj:${id}`]);
    this.flash(`Added ${kind}`);
  }

  private addLight(kind: 'point' | 'directional' | 'ambient'): void {
    const json = this.state.get().json;
    const id = `light-${(json.lights?.length ?? 0) + 1}`;
    let light: LightDef;
    if (kind === 'point') {
      light = { type: 'point', id, position: [0, 4, 0], color: '#ffffff', intensity: 1.0, range: 20, decay: 2 };
    } else if (kind === 'directional') {
      light = { type: 'directional', id, direction: [0.5, 1, 0.5], color: '#fff5d8', intensity: 1.6 };
    } else {
      light = { type: 'ambient', id, color: '#ffffff', intensity: 0.4 };
    }
    this.state.commit({ ...json, lights: [...(json.lights ?? []), light] }, { kind: 'structural' });
    this.state.setSelection([`light:${id}`]);
    this.flash(`Added ${kind} light`);
  }

  private spawnModelRef(src: string, displayName: string): void {
    const json = this.state.get().json;
    const id = nextUniqueId(json, 'model');
    const p = this.viewport.spawnTarget();
    const node: ModelRefObject = {
      type: 'modelRef', id,
      transform: { p },
      src,
      scaleToMaxDim: 2,
      yawDeg: 0,
      name: displayName
    };
    this.state.commit({ ...json, objects: [...(json.objects ?? []), node] }, { kind: 'structural' });
    this.state.setSelection([`obj:${id}`]);
  }

  private duplicateSelection(): void {
    const json = this.state.get().json;
    const ids = this.state.get().selectionIds.filter((s) => s.startsWith('obj:')).map((s) => s.slice(4));
    if (ids.length === 0) { this.flash('Nothing to duplicate', true); return; }
    const newIds: string[] = [];
    let next = json;
    for (const id of ids) {
      const path = findObjectPath(next.objects ?? [], id);
      if (!path) continue;
      const cloned = structuredClone(path[path.length - 1]!.node);
      const newId = nextUniqueId(next, ((cloned as { type: string }).type) + '-copy');
      cloned.id = newId;
      // Append to the same parent (sibling of source).
      next = insertSibling(next, path, cloned);
      newIds.push(`obj:${newId}`);
    }
    this.state.commit(next, { kind: 'structural' });
    this.state.setSelection(newIds);
    this.flash(`Duplicated ${newIds.length}`);
  }

  private deleteSelection(): void {
    const json = this.state.get().json;
    const ids = this.state.get().selectionIds;
    if (ids.length === 0) { this.flash('Nothing to delete', true); return; }
    let next = json;
    let removed = 0;
    for (const sid of ids) {
      if (sid.startsWith('obj:')) {
        const id = sid.slice(4);
        const path = findObjectPath(next.objects ?? [], id);
        if (path) { next = removeAtPath(next, path); removed += 1; }
      } else if (sid.startsWith('light:')) {
        const id = sid.slice(6);
        const idx = (next.lights ?? []).findIndex((l) => (l.id ?? `(${l.type})`) === id);
        if (idx >= 0) {
          const lights = (next.lights ?? []).slice();
          lights.splice(idx, 1);
          next = { ...next, lights };
          removed += 1;
        }
      } else if (sid.startsWith('behavior:')) {
        const id = sid.slice(9);
        const idx = (next.behaviors ?? []).findIndex((b) => b.id === id);
        if (idx >= 0) {
          const beh = (next.behaviors ?? []).slice();
          beh.splice(idx, 1);
          next = { ...next, behaviors: beh };
          removed += 1;
        }
      }
    }
    this.state.commit(next, { kind: 'structural' });
    this.state.clearSelection();
    if (removed > 0) this.flash(`Deleted ${removed}`);
  }

  private groupSelection(): void {
    const json = this.state.get().json;
    const ids = this.state.get().selectionIds.filter((s) => s.startsWith('obj:')).map((s) => s.slice(4));
    if (ids.length < 2) { this.flash('Select 2+ objects to group', true); return; }
    // Verify all share the same parent path. (For v1, only top-level
    // selections are groupable to avoid path-rewriting tangles.)
    const objectsArr = json.objects ?? [];
    const indices: number[] = [];
    for (const id of ids) {
      const i = objectsArr.findIndex((n) => (n.id ?? `(${n.type})`) === id);
      if (i < 0) { this.flash('Group only works on top-level objects in v1', true); return; }
      indices.push(i);
    }
    indices.sort((a, b) => a - b);
    const groupId = nextUniqueId(json, 'group');
    const children = indices.map((i) => objectsArr[i]!);
    const remaining = objectsArr.filter((_, i) => !indices.includes(i));
    const group: GroupObject = { type: 'group', id: groupId, children };
    this.state.commit({ ...json, objects: [...remaining, group] }, { kind: 'structural' });
    this.state.setSelection([`obj:${groupId}`]);
    this.flash('Grouped');
  }

  private ungroupSelection(): void {
    const json = this.state.get().json;
    const sid = this.state.get().primarySelectionId;
    if (!sid.startsWith('obj:')) return;
    const id = sid.slice(4);
    const objectsArr = json.objects ?? [];
    const i = objectsArr.findIndex((n) => (n.id ?? `(${n.type})`) === id);
    if (i < 0) { this.flash('Group only works on top-level groups in v1', true); return; }
    const target = objectsArr[i]!;
    if (target.type !== 'group') { this.flash('Selection is not a group', true); return; }
    const children = target.children ?? [];
    const next = [...objectsArr.slice(0, i), ...children, ...objectsArr.slice(i + 1)];
    this.state.commit({ ...json, objects: next }, { kind: 'structural' });
    this.state.setSelection(children.filter((c) => c.id).map((c) => `obj:${c.id}`));
    this.flash('Ungrouped');
  }

  private exit(): void {
    const r = this.resolveExit;
    this.resolveExit = null;
    this.dispose();
    r?.();
  }

  // ---------- keyboard ----------

  private onKey(e: KeyboardEvent): void {
    // Ignore keys when typing in form fields.
    const target = e.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT')) {
      // Still allow ctrl+z / cmd+z while typing (system convention).
      if ((e.ctrlKey || e.metaKey) && e.code === 'KeyZ') { /* fall through */ } else return;
    }
    const mod = e.ctrlKey || e.metaKey;
    if (mod && e.code === 'KeyZ' && !e.shiftKey) { e.preventDefault(); this.state.undo(); return; }
    if (mod && (e.code === 'KeyY' || (e.code === 'KeyZ' && e.shiftKey))) { e.preventDefault(); this.state.redo(); return; }
    if (mod && e.code === 'KeyD') { e.preventDefault(); this.duplicateSelection(); return; }
    if (mod && e.code === 'KeyG' && e.shiftKey) { e.preventDefault(); this.ungroupSelection(); return; }
    if (mod && e.code === 'KeyG') { e.preventDefault(); this.groupSelection(); return; }
    if (e.code === 'Delete' || e.code === 'Backspace') {
      // Don't fight back-button on inputs (already returned above).
      e.preventDefault(); this.deleteSelection(); return;
    }
    // Tool-mode hotkeys are suppressed while pointer-locked so WASD/QE in
    // fly mode don't double-fire as mode-switches (W kicks out otherwise).
    if (!this.viewport.fly.isLocked()) {
      if (e.code === 'KeyT') { this.tool.setMode('control'); return; }
      if (e.code === 'KeyQ') { this.tool.setMode('select'); return; }
      if (e.code === 'KeyW') { this.tool.setMode('translate'); return; }
      if (e.code === 'KeyE') { this.tool.setMode('rotate'); return; }
      if (e.code === 'KeyR') { this.tool.setMode('scale'); return; }
    }
    if (e.code === 'KeyF') { this.viewport.frameSelection(); return; }
    if (e.code === 'Escape') { this.state.clearSelection(); return; }
  }
}

// ---------- helpers ----------

function nextUniqueId(json: MapJSON, prefix: string): string {
  const existing = new Set<string>();
  const visit = (nodes: ObjectNode[] | undefined) => {
    if (!nodes) return;
    for (const n of nodes) {
      if (n.id) existing.add(n.id);
      if (n.type === 'group') visit(n.children);
    }
  };
  visit(json.objects);
  let i = 1;
  while (existing.has(`${prefix}-${i}`)) i++;
  return `${prefix}-${i}`;
}

interface ObjectPathStep { index: number; node: ObjectNode; }
type ObjectPath = ObjectPathStep[];

function findObjectPath(objects: ObjectNode[], id: string): ObjectPath | null {
  for (let i = 0; i < objects.length; i++) {
    const n = objects[i]!;
    const nId = n.id ?? `(${n.type})`;
    if (nId === id) return [{ index: i, node: n }];
    if (n.type === 'group' && n.children?.length) {
      const sub = findObjectPath(n.children, id);
      if (sub) return [{ index: i, node: n }, ...sub];
    }
  }
  return null;
}

function insertSibling(json: MapJSON, path: ObjectPath, sibling: ObjectNode): MapJSON {
  const rootArr = (json.objects ?? []).slice();
  let arr = rootArr;
  for (let i = 0; i < path.length - 1; i++) {
    const step = path[i]!;
    const parent = arr[step.index]! as ObjectNode;
    if (parent.type !== 'group') break;
    const children = (parent.children ?? []).slice();
    arr[step.index] = { ...parent, children };
    arr = children;
  }
  arr.push(sibling);
  return { ...json, objects: rootArr };
}

function removeAtPath(json: MapJSON, path: ObjectPath): MapJSON {
  const rootArr = (json.objects ?? []).slice();
  let arr = rootArr;
  for (let i = 0; i < path.length - 1; i++) {
    const step = path[i]!;
    const parent = arr[step.index]! as ObjectNode;
    if (parent.type !== 'group') break;
    const children = (parent.children ?? []).slice();
    arr[step.index] = { ...parent, children };
    arr = children;
  }
  arr.splice(path[path.length - 1]!.index, 1);
  return { ...json, objects: rootArr };
}

const INLINE_MAP_KEYS = ['mapColor', 'mapNormal', 'mapRoughness', 'mapMetalness', 'mapAo', 'mapEmissive'] as const;

function resolveAssetUrlsInPlace(json: MapJSON): void {
  if (json.ibl?.hdrPath && isAssetUrl(json.ibl.hdrPath)) {
    json.ibl.hdrPath = resolveAssetUrl(json.ibl.hdrPath);
  }
  if (json.pickerCard?.thumbnail && isAssetUrl(json.pickerCard.thumbnail)) {
    json.pickerCard.thumbnail = resolveAssetUrl(json.pickerCard.thumbnail);
  }
  const walk = (nodes: ObjectNode[] | undefined): void => {
    if (!nodes) return;
    for (const n of nodes) {
      if (n.type === 'modelRef' && isAssetUrl(n.src)) {
        (n as { src: string }).src = resolveAssetUrl(n.src);
      }
      const m = n.material as Record<string, unknown> | undefined;
      if (m) {
        for (const key of INLINE_MAP_KEYS) {
          const cur = m[key];
          if (typeof cur === 'string' && isAssetUrl(cur)) {
            m[key] = resolveAssetUrl(cur);
          }
        }
      }
      if (n.type === 'group') walk(n.children);
    }
  };
  walk(json.objects);
}
